import ProjectHelper from 'src/app/helpers/project-helper';
import Company from 'src/app/model/company/company';
import PlacementInvoice from 'src/app/model/invoice/placement-invoice';
import { Project } from 'src/app/model/project/project';
import { SubProject } from 'src/app/model/project/sub-project';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { ContactPickerEvent } from 'src/app/shared/contact-picker/contact-picker-event';

export class EditProjectHelper {
  private constructor() {}

  public static handleSaveContacts(
    event: ContactPickerEvent,
    project: Project,
    model: SubProject,
    projectDisplayObservableService: ProjectDisplayObservableService
  ): void {
    let selectedContacts =
      event.projectContacts == null
        ? []
        : event.projectContacts.filter(pc => pc.id != null).map(pc => pc.id);
    let deletedContacts =
      project.projectContacts == null
        ? []
        : project.projectContacts
            .filter(pc => pc.id != null && !selectedContacts.includes(pc.id))
            .map(pc => pc.id);
    let selectedCompanies =
      event == null
        ? []
        : event.projectContacts
            .map(pc => (pc.company == null ? null : pc.company.id))
            .filter(c => c != null);
    let deletedCompanies =
      project.projectContacts == null
        ? []
        : project.projectContacts
            .filter(
              pc =>
                pc.company == null || !selectedCompanies.includes(pc.company.id)
            )
            .map(pc => (pc.company == null ? null : pc.company.id));
    project.projectContacts = event.projectContacts;
    if (event.replacedCompanies != null && event.replacedCompanies.size > 0) {
      this.processReplacedCompanies(event, project, model);
    }
    this.setPlacementInvoices(model, project, projectDisplayObservableService);
    if (deletedCompanies.length > 0) {
      this.deleteInvoicesFromProject(
        model,
        project,
        deletedCompanies,
        projectDisplayObservableService
      );
    }
    if (deletedContacts.length > 0) {
      this.deleteProductPhoneNumbers(
        project,
        deletedContacts,
        projectDisplayObservableService
      );
    }
  }

  private static processReplacedCompanies(
    event: ContactPickerEvent,
    project: Project,
    model: SubProject
  ): void {
    const companies = ProjectHelper.getUniqueCompaniesFromProjectContacts(
      event.projectContacts
    );
    event.replacedCompanies.forEach((v, k) => {
      if (!companies.includes(k)) {
        this.doReplaceInvoiceCompanyInSubProject(model, k, v);
        if (project.subProjects != null) {
          project.subProjects.forEach(sp => {
            if (sp.id !== model.id) {
              this.doReplaceInvoiceCompanyInSubProject(sp, k, v);
            }
          });
        }
      }
    });
  }

  private static doReplaceInvoiceCompanyInSubProject(
    subProject: SubProject,
    replacedCompany: number,
    replacementCompany: Company
  ): void {
    if (subProject.projectInvoices != null) {
      subProject.projectInvoices.forEach(pi => {
        if (pi.company != null && pi.company.id === replacedCompany) {
          pi.company = replacementCompany;
        }
      });
    }
  }

  public static setPlacementInvoices(
    model: SubProject,
    project: Project,
    projectDisplayObservableService: ProjectDisplayObservableService
  ): void {
    if (model.projectInvoices == null) {
      model.projectInvoices = [];
    }
    let companiesWithInvoices = new Set<number>();
    for (let invoice of model.projectInvoices) {
      if (invoice.type === 'PlacementInvoice') {
        companiesWithInvoices.add(invoice.company.id);
      }
    }
    for (let pc of project.projectContacts) {
      if (!companiesWithInvoices.has(pc.company.id)) {
        let placementInvoice = new PlacementInvoice(pc.company);
        placementInvoice.priceLines = [];
        placementInvoice.customProductInvoicePrices = [];
        model.projectInvoices.push(placementInvoice);
        companiesWithInvoices.add(pc.company.id);
      }
    }
    projectDisplayObservableService
      .getProjectInvoicesChangedSubscribable()
      .next();
  }

  private static deleteInvoicesFromProject(
    model: SubProject,
    project: Project,
    toDeleteCompanies: number[],
    projectDisplayObservableService: ProjectDisplayObservableService
  ) {
    this.doDeleteInvoicesFromSubProject(model, toDeleteCompanies);
    if (project.subProjects != null) {
      project.subProjects.forEach(sp => {
        if (sp.id !== model.id) {
          this.doDeleteInvoicesFromSubProject(sp, toDeleteCompanies);
        }
      });
    }
    projectDisplayObservableService
      .getProjectInvoicesChangedSubscribable()
      .next();
  }

  private static doDeleteInvoicesFromSubProject(
    subProject: SubProject,
    toDeleteCompanies: number[]
  ) {
    if (subProject.projectInvoices != null) {
      for (let i = subProject.projectInvoices.length - 1; i >= 0; i--) {
        let projectInvoice = subProject.projectInvoices[i];
        if (
          projectInvoice.company == null ||
          toDeleteCompanies.includes(projectInvoice.company.id)
        ) {
          subProject.projectInvoices.splice(i, 1);
        }
      }
    }
  }

  private static deleteProductPhoneNumbers(
    project: Project,
    deletedContacts: number[],
    projectDisplayObservableService: ProjectDisplayObservableService
  ) {
    if (project.products != null) {
      project.products.forEach(p => {
        if (p.productPhoneNumbers != null) {
          for (let i = p.productPhoneNumbers.length - 1; i >= 0; i--) {
            let productPhoneNumber = p.productPhoneNumbers[i];
            if (
              productPhoneNumber.projectContact != null &&
              deletedContacts.includes(productPhoneNumber.projectContact.id)
            ) {
              p.productPhoneNumbers.splice(i, 1);
            }
          }
        }
      });
      projectDisplayObservableService
        .getProductPhoneNumberChangedSubscribable()
        .next();
    }
  }
}
